import * as Sentry from "@sentry/browser";

/**
 * Extracts a clean path from a URL for the span name
 * @param url - The full URL
 * @returns The path portion of the URL (e.g., "/api/data" from "https://example.com/api/data?foo=bar")
 */
function getUrlPath(url: string): string {
    try {
        const urlObj = new URL(url, window.location.origin);
        return urlObj.pathname;
    } catch {
        // If URL parsing fails, return the original string
        return url;
    }
}

/**
 * A wrapper around the native fetch API that creates Sentry APM spans for tracking performance.
 * This function automatically tracks fetch requests when Sentry tracing is enabled.
 *
 * @param input - The resource URL or Request object
 * @param init - Optional fetch configuration
 * @returns Promise that resolves to the Response
 *
 * @example
 * // Simple GET request
 * const response = await trackedFetch('/api/data');
 *
 * @example
 * // POST with options
 * const response = await trackedFetch('/api/submit', {
 *   method: 'POST',
 *   body: JSON.stringify(data),
 *   headers: { 'Content-Type': 'application/json' }
 * });
 */
export async function trackedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
): Promise<Response> {
    // Check if tracing is enabled - if not, use native fetch
    if (!window.errorTracker?.isTracingAllowed) {
        return fetch(input, init);
    }

    // Extract URL and method for span metadata
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method?.toUpperCase() || (input instanceof Request ? input.method.toUpperCase() : "GET");
    const urlPath = getUrlPath(url);

    // Create a Sentry span to track this fetch request
    const span = Sentry.startSpanManual({
        op: "http.client",
        name: `${ method } ${ urlPath }`,
        attributes: {
            "http.method": method,
            "http.url": url
        }
    }, (sentrySpan) => sentrySpan);

    try {
        const response = await fetch(input, init);

        // Add response status to span
        if (span) {
            span.setAttribute("http.status_code", response.status);
        }

        return response;
    } catch (error) {
        // Mark span as errored
        if (span) {
            span.setStatus({ code: 2, message: "unknown_error" }); // SpanStatusCode.ERROR = 2
        }
        throw error;
    } finally {
        // Always end the span
        span?.end();
    }
}
